import React, { useState, useRef, useEffect } from 'react';

const SUGGESTIONS = [
  'Schreib ein YouTube-Skript für dieses Projekt',
  'Erstelle eine Szenenübersicht mit Regie-Notizen',
  'Formuliere einen packenden Hook für den Intro',
  'Schlag 5 kreative Bildideen vor',
];

function Message({ msg, onAddToProject }) {
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    onAddToProject(msg.text);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
      {msg.role === 'assistant' && (
        <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8S4.41 14.5 8 14.5 14.5 11.59 14.5 8 11.59 1.5 8 1.5zm0 2a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 9.5c-2 0-3.77-1.03-4.8-2.58.02-.99 3.2-1.53 4.8-1.53s4.78.54 4.8 1.53C11.77 12.47 10 13.5 8 13.5z" fill="white"/>
          </svg>
        </div>
      )}

      <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
        {msg.image && (
          <img src={msg.image} alt="" className="rounded-xl mb-2 max-h-48 object-cover border border-gray-100" />
        )}
        <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          msg.role === 'user'
            ? 'bg-indigo-600 text-white rounded-tr-sm'
            : 'bg-white border border-gray-100 text-gray-700 rounded-tl-sm shadow-sm'
        }`}>
          {msg.text}
        </div>
        {msg.role === 'assistant' && (
          <button
            onClick={handleAdd}
            className={`mt-1.5 text-xs flex items-center gap-1 px-2.5 py-1 rounded-lg transition-all ${
              added ? 'text-emerald-600 bg-emerald-50' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'
            }`}
          >
            {added ? (
              <>
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Zum Projekt hinzugefügt
              </>
            ) : (
              <>
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 1V9M1 5H9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                Zum Projekt hinzufügen
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AIStudio({ project, onChange }) {
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { dataUrl, base64, mediaType }
  const [error, setError]         = useState('');
  const bottomRef                 = useRef(null);
  const fileRef                   = useRef(null);
  const taRef                     = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64  = dataUrl.split(',')[1];
      setPendingImage({ dataUrl, base64, mediaType: file.type });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const sendMessage = async (textOverride) => {
    const text = (textOverride || input).trim();
    if (!text && !pendingImage) return;
    setError('');

    const userMsg = { role: 'user', text, image: pendingImage?.dataUrl || null };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setPendingImage(null);
    setLoading(true);

    // Build API messages (last 20 for context)
    const apiMessages = newMessages.slice(-20).map(m => {
      if (m.role === 'user' && m.image) {
        return {
          role: 'user',
          content: [
            { type: 'image', source: { type: 'base64', media_type: pendingImage?.mediaType || 'image/jpeg', data: m.image.split(',')[1] } },
            { type: 'text', text: m.text || 'Was siehst du auf diesem Bild? Wie kann ich es für mein Video-Projekt nutzen?' },
          ],
        };
      }
      return { role: m.role, content: m.text };
    });

    try {
      const res = await fetch('/.netlify/functions/claude-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          system: `Du bist ein kreativer Video-Produktions-Assistent für das Projekt "${project?.title || 'FrameIt'}". Du hilfst beim Schreiben von Skripten, Szenenplanung, Regie-Notizen und der Analyse von Bildern. Formatiere Skripte in gut lesbarem Markdown. Antworte auf Deutsch.`,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler bei der Anfrage');

      setMessages(prev => [...prev, { role: 'assistant', text: data.content }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addToProject = (text) => {
    const nb = { id: Date.now(), type: 'text', content: text, imageUrl: '', imageCaption: '' };
    onChange({ ...project, blocks: [...(project.blocks || []), nb] });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ height: 'calc(100vh - 56px)' }} className="flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-6 py-3 flex items-center gap-3">
        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <path d="M2 8s2-5 6-5 6 5 6 5-2 5-6 5-6-5-6-5z" stroke="white" strokeWidth="1.3"/>
            <circle cx="8" cy="8" r="2" fill="white"/>
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">KI-Studio</p>
          <p className="text-xs text-gray-400">Intern · Nicht für Kunden sichtbar</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="max-w-2xl mx-auto flex flex-col gap-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 mx-auto mb-4 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3a2.5 2.5 0 110 5 2.5 2.5 0 010-5zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="#6366f1"/>
                </svg>
              </div>
              <h3 className="text-base font-semibold text-gray-800 mb-1">KI-Assistent für dein Projekt</h3>
              <p className="text-sm text-gray-400 mb-6">Schreib Skripte, plane Szenen oder lass Bilder analysieren.</p>
              <div className="grid grid-cols-2 gap-2">
                {SUGGESTIONS.map(s => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="text-left text-xs text-gray-600 bg-white border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 px-3 py-2.5 rounded-xl transition-all leading-snug"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <Message key={i} msg={msg} onAddToProject={addToProject} />
          ))}

          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="animate-spin w-3 h-3 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
              </div>
              <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                <div className="flex gap-1 items-center h-4">
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-600">
              Fehler: {error}
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-gray-100 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          {pendingImage && (
            <div className="mb-2 flex items-center gap-2">
              <img src={pendingImage.dataUrl} alt="" className="h-14 w-14 object-cover rounded-lg border border-gray-100" />
              <button onClick={() => setPendingImage(null)} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
              </button>
            </div>
          )}
          <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 focus-within:border-indigo-300 focus-within:ring-2 focus-within:ring-indigo-50 transition-all">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageFile}
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="flex-shrink-0 w-8 h-8 rounded-xl border border-gray-200 hover:border-indigo-300 hover:text-indigo-500 text-gray-400 flex items-center justify-center transition-all"
              title="Bild hochladen"
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="2" width="16" height="16" rx="3" stroke="currentColor" strokeWidth="1.4"/>
                <circle cx="7" cy="7" r="1.8" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M2 14l5-5 4 4 3-3 4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
            </button>
            <textarea
              ref={taRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Skript schreiben, Szene planen, Bild analysieren… (Enter zum Senden)"
              className="flex-1 bg-transparent text-sm text-gray-700 outline-none resize-none leading-relaxed placeholder-gray-300 max-h-40"
              rows={1}
              style={{ height: 'auto' }}
              onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={loading || (!input.trim() && !pendingImage)}
              className="flex-shrink-0 w-8 h-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-200 disabled:cursor-not-allowed flex items-center justify-center transition-all mb-0.5"
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M7 12V2M2 7l5-5 5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-300 text-center mt-2">Shift+Enter für neue Zeile · Bilder werden mit Claude Vision analysiert</p>
        </div>
      </div>
    </div>
  );
}
